import { useLayoutEffect, type ReactNode } from "react";
import { create } from "zustand";
import type { AgentFocus } from "@/types/agentFocus.type";
import type { ReasoningEffort } from "@/types/chat.type";

export type AgentSendOptions = {
  /** Forces the model for this turn only; never touches the persisted picker. */
  modelId?: string;
  reasoningEffort?: ReasoningEffort;
  /** Start a brand-new chat for this turn, ignoring the currently open one. */
  newSession?: boolean;
  /** Send into this chat instead of the open one. */
  sessionId?: string;
  /**
   * Files that go with the message (the strategy start's PDF). They need a
   * chat to belong to, so this always starts a new one, uploads them into it,
   * and sends once they are read.
   */
  files?: File[];
};

type AgentFocusStore = {
  /** Where the user is. Pages publish it; the rail sends it with every message. */
  focus: AgentFocus;
  /** The page manages its own scroll regions, so the shell must not also scroll. */
  contained: boolean;
  setFocus: (focus: AgentFocus, opts?: { contained?: boolean }) => void;
  clearFocus: () => void;
};

const HOME: AgentFocus = { surface: "home" };

export const useAgentFocusStore = create<AgentFocusStore>()((set) => ({
  focus: HOME,
  contained: false,
  setFocus: (focus, opts) => set({ focus, contained: opts?.contained === true }),
  clearFocus: () => set({ focus: HOME, contained: false }),
}));

/**
 * How a page hands the rail a message or a draft.
 *
 * Deliberately a registered handler rather than a queue in the store: a queue
 * has to be drained from an effect, and draining it means calling the rail's
 * own setState from inside one. This way the page's click runs the rail's
 * handler directly, in the same tick, with nothing to replay.
 */
type AgentRailHandlers = {
  send: (text: string, opts?: AgentSendOptions) => void;
  prefill: (text: string, opts?: { newChat?: boolean }) => void;
  replyTo: (target: AgentReplyTarget) => void;
  /** Clears the reply when `key` owns it (or always, without a key). */
  clearReply: (key?: string) => void;
  ask: (ask: AgentLocalAsk) => void;
  /** Clears the local card when `key` owns it (or always, without a key). */
  clearAsk: (key?: string) => void;
  /** Stops the running turn; what was saved stays. */
  stop: () => void;
};

/**
 * A page's own question, put up in the rail with no model call ("Add a post on
 * Thu 17 Dec"). The rail shows `prompt` as the person's message and `card`
 * under M7's mark, at the end of the chat; nothing reaches the agent. The card
 * calls `done` (from the rail) when it is finished or cancelled.
 */
export type AgentLocalAsk = {
  /** Owner of the card, so a page can clear only its own. */
  key: string;
  /** The person's message, as their bubble. */
  prompt: string;
  card: (done: () => void) => ReactNode;
};

/**
 * A direction typed in the chat for one thing on the page ("Replying to Idea
 * 2"). The composer shows the quote chip, and sending hands the words to
 * `onSend` instead of the chat; nothing is sent until the person sends it.
 */
export type AgentReplyTarget = {
  /** Owner of the target, so a page can clear only its own. */
  key: string;
  /** "Replying to Idea 2". */
  label: string;
  /** The quoted line under the label, the idea's title. */
  quote: string;
  placeholder?: string;
  onSend: (text: string) => void;
};

let handlers: AgentRailHandlers | null = null;

/** Called by the rail on mount; cleared on unmount. */
export function registerAgentRailHandlers(next: AgentRailHandlers | null) {
  handlers = next;
}

export const agentRail = {
  send: (text: string, opts?: AgentSendOptions) => handlers?.send(text, opts),
  /** `newChat` switches to a fresh chat first, so the draft does not land in the open one. */
  prefill: (text: string, opts?: { newChat?: boolean }) => handlers?.prefill(text, opts),
  /** False when no rail is mounted to take the reply. */
  replyTo: (target: AgentReplyTarget): boolean => {
    if (!handlers) return false;
    handlers.replyTo(target);
    return true;
  },
  clearReply: (key?: string) => handlers?.clearReply(key),
  /** False when no rail is mounted to take the card; the page then uses its own dialog. */
  ask: (ask: AgentLocalAsk): boolean => {
    if (!handlers) return false;
    handlers.ask(ask);
    return true;
  },
  clearAsk: (key?: string) => handlers?.clearAsk(key),
  /** Stop the running turn (the page's own Stop, e.g. under a slide being written). */
  stop: () => handlers?.stop(),
};

/**
 * One line a page asks the rail to show under its head (Strategy: "Writing
 * chapter 4 of 12."). The page owns it while it is mounted.
 */
export const useAgentRailStatusStore = create<{ line: string | null; set: (line: string | null) => void }>()((set) => ({
  line: null,
  set: (line) => set({ line }),
}));

/**
 * Publish this page's focus for as long as it is mounted.
 *
 * The rail lives in the shell and never unmounts, so a page cannot hand it
 * props — it announces where the user is instead, and leaving the page falls
 * back to Home rather than leaving the agent pointed at a screen nobody is on.
 *
 * The focus travels through the effect as a JSON string: pages rebuild the
 * object every render, so a reference dependency would re-publish endlessly.
 */
export function useAgentFocus(focus: AgentFocus, opts?: { contained?: boolean }) {
  const serialized = JSON.stringify(focus);
  const contained = opts?.contained === true;

  useLayoutEffect(() => {
    useAgentFocusStore
      .getState()
      .setFocus(JSON.parse(serialized) as AgentFocus, { contained });
    return () => useAgentFocusStore.getState().clearFocus();
  }, [serialized, contained]);
}
